import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { UserProfileFacade } from '@cpd-app/state/user-profile';
import { UserProfile } from '@cpd-app/core-module/models';
import { UtilityService } from '@cpd-app/core-module/services';

import { RegistrationDraftService } from './registration-draft.service';

/**
 * Applies what registration collected, once there is an account to apply it to.
 *
 * The design's registration runs branch and installation before "Create my
 * account", but sign-up here is a magic link: the account does not exist until
 * the member opens the email. So step two's answers are held in the draft and
 * written to the profile on the first authenticated load after it.
 *
 * Each field goes through the mutation that already owns it rather than a new
 * registration write, so a value captured at sign-up and the same value edited
 * later in the survey take exactly one code path.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationDraftFlushService {
  constructor(
    private cognitoAuthFacade: CognitoAuthFacade,
    private userProfileFacade: UserProfileFacade,
    private draft: RegistrationDraftService
  ) {}

  public flushWhenAuthenticated(): void {
    const held = this.draft.read();

    if (!held.branchCode && !held.installationId && !held.separationMonth) {
      return;
    }

    this.cognitoAuthFacade.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        take(1)
      )
      .subscribe(() => this.apply());
  }

  private apply(): void {
    const held = this.draft.read();

    if (held.branchCode) {
      this.userProfileFacade.userProfile$
        .pipe(
          filter((profile) => !!profile),
          take(1)
        )
        .subscribe((profile) => {
          // cloneDeep, not a spread: UserProfile is a class and mpcCodeList is
          // a getter on it, which a spread would drop.
          const next = UtilityService.cloneObject<UserProfile>(profile);
          next.militaryBranch = {
            ...next.militaryBranch,
            branchCode: held.branchCode
          };

          this.userProfileFacade.saveUserSkillsSurvey(next);
        });
    }

    if (held.installationId) {
      this.userProfileFacade.saveMilitaryInstallation(held.installationId);
    }

    if (held.separationMonth) {
      const months = RegistrationDraftFlushService.monthsUntil(
        held.separationMonth
      );

      if (months !== null) {
        this.userProfileFacade.updateUserProfileMilitaryTransition(months);
      }
    }

    this.draft.clear();
  }

  /**
   * The design asks for a month; SaveUserMilitaryTermination stores
   * numberOfMonthsOfTermination, a count from now. Converting here is the whole
   * of that mismatch, and it is lossy: the stored count is only true on the day
   * it is written. Widening UserTerminationInput to carry the date itself is
   * the real fix and is backend work.
   *
   * Returns null for an unparseable value so a bad month is not silently stored
   * as "separating now".
   */
  public static monthsUntil(month: string): number | null {
    const match = /^(\d{4})-(\d{2})$/.exec(month || '');

    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;

    if (monthIndex < 0 || monthIndex > 11) {
      return null;
    }

    const now = new Date();
    const months =
      (year - now.getFullYear()) * 12 + (monthIndex - now.getMonth());

    // A date already past means separated, not negative months of service left.
    return Math.max(months, 0);
  }
}
